import React, { useEffect, useState, useMemo } from 'react';
import { DataGrid, GridColDef, GridValueGetterParams } from '@mui/x-data-grid';
import { api, Product } from '../services/api';
import { 
  Box, 
  Typography, 
  Paper, 
  Alert, 
  CircularProgress,
  TextField,
  InputAdornment,
  IconButton,
  Stack,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  SelectChangeEvent,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
} from '@mui/material';
import { useLanguage } from '../contexts/LanguageContext';
import { Search, X, Edit, Trash2 } from 'lucide-react';

interface ProductListProps {
  companyId: string;
}

export const ProductList: React.FC<ProductListProps> = ({ companyId }) => {
  const { t } = useLanguage();
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterField, setFilterField] = useState('all');
  const [editProduct, setEditProduct] = useState<Product | null>(null);
  const [deleteProduct, setDeleteProduct] = useState<Product | null>(null);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const handleEditClick = (product: Product) => {
    setEditProduct(product);
    setEditDialogOpen(true);
  };

  const handleDeleteClick = (product: Product) => {
    setDeleteProduct(product);
    setDeleteDialogOpen(true);
  };

  const handleEditClose = () => {
    setEditProduct(null);
    setEditDialogOpen(false);
  };

  const handleDeleteClose = () => {
    setDeleteProduct(null);
    setDeleteDialogOpen(false);
  };

  const handleEditSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editProduct) return;

    setIsUpdating(true);
    try {
      await api.updateProduct(editProduct._id, {
        name: editProduct.name,
        code: editProduct.code,
        description: editProduct.description || '',
        purchasePrice: editProduct.purchasePrice,
        salePrice: editProduct.salePrice,
        quantity: editProduct.quantity,
        companyId: companyId
      });
      
      handleEditClose();
      window.location.reload();
    } catch (error) {
      console.error('Error updating product:', error);
      setError(error instanceof Error ? error.message : 'Failed to update product');
    } finally {
      setIsUpdating(false);
    }
  };

  const handleDeleteConfirm = async () => {
    if (!deleteProduct) return;

    setIsDeleting(true);
    try {
      await api.deleteProduct(deleteProduct._id);
      handleDeleteClose();
      window.location.reload();
    } catch (error) {
      console.error('Error deleting product:', error);
      setError(error instanceof Error ? error.message : 'Failed to delete product');
    } finally {
      setIsDeleting(false);
    }
  };

  const handleEditInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!editProduct) return;
    const { name, value } = e.target;
    setEditProduct(prev => {
      if (!prev) return prev;
      return {
        ...prev,
        [name]: name === 'purchasePrice' || name === 'salePrice' || name === 'quantity'
          ? Number(value)
          : value
      };
    });
  };

  const columns: GridColDef[] = [
    { 
      field: '__index',
      headerName: 'N°', 
      width: 70,
      renderCell: (params) => {
        return filteredProducts.indexOf(params.row) + 1;
      }
    },
    { field: 'code', headerName: t.productCode, width: 120, flex: 1 },
    { field: 'name', headerName: t.productName, width: 180, flex: 1 },
    { field: 'description', headerName: t.description, width: 200, flex: 1.5, valueGetter: (params: GridValueGetterParams) => params.row.description || 'N/A' },
    { 
      field: 'purchasePrice', 
      headerName: t.purchasePrice, 
      width: 130,
      flex: 1,
      valueGetter: (params: GridValueGetterParams) => {
        const currency = params.row.companyId?.signCurrency || 'FC';
        return `${params.row.purchasePrice} ${currency}`;
      }
    },
    { 
      field: 'salePrice', 
      headerName: t.salePrice, 
      width: 130,
      flex: 1,
      valueGetter: (params: GridValueGetterParams) => {
        const currency = params.row.companyId?.signCurrency || 'FC';
        return `${params.row.salePrice} ${currency}`;
      }
    },
    { field: 'quantity', headerName: t.quantity, width: 100, flex: 0.8 },
    { 
      field: 'profit', 
      headerName: 'Marge', 
      width: 130,
      flex: 1,
      valueGetter: (params: GridValueGetterParams) => {
        const profit = params.row.salePrice - params.row.purchasePrice;
        const currency = params.row.companyId?.signCurrency || 'FC';
        return `${profit} ${currency}`;
      }
    },
    { 
      field: 'actions',
      headerName: 'Actions',
      width: 120,
      sortable: false,
      renderCell: (params) => (
        <Stack direction="row" spacing={1}>
          <IconButton
            onClick={() => handleEditClick(params.row)}
            size="small"
            color="primary"
          >
            <Edit size={18} />
          </IconButton>
          <IconButton
            onClick={() => handleDeleteClick(params.row)}
            size="small"
            color="error"
          >
            <Trash2 size={18} />
          </IconButton>
        </Stack>
      ),
    }
  ];

  useEffect(() => {
    const fetchProducts = async () => {
      try {
        setLoading(true);
        setError(null);
        console.log('Fetching products for company ID:', companyId);
        const response = await api.getProductsByCompany(companyId);
        console.log('Received products:', response);
        
        if (Array.isArray(response)) {
          setProducts(response);
        } else {
          console.error('Invalid products data format:', response);
          setError('Invalid data format received from server');
          setProducts([]);
        }
      } catch (error) {
        console.error('Failed to fetch products:', error);
        setError(error instanceof Error ? error.message : 'Failed to load products');
        setProducts([]);
      } finally {
        setLoading(false);
      }
    };

    if (companyId) {
      fetchProducts();
    } else {
      console.error('No company ID provided');
      setError('No company ID provided');
      setLoading(false);
    }
  }, [companyId]);

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(event.target.value);
  };

  const handleFilterChange = (event: SelectChangeEvent) => {
    setFilterField(event.target.value);
  };

  const clearSearch = () => {
    setSearchTerm('');
  };

  const filteredProducts = useMemo(() => {
    return products.filter(product => {
      const searchLower = searchTerm.toLowerCase();
      
      if (!searchTerm) return true;

      switch (filterField) {
        case 'code':
          return product.code.toLowerCase().includes(searchLower);
        case 'name':
          return product.name.toLowerCase().includes(searchLower);
        case 'description':
          return product.description?.toLowerCase().includes(searchLower);
        case 'all':
        default:
          return (
            product.code.toLowerCase().includes(searchLower) ||
            product.name.toLowerCase().includes(searchLower) ||
            product.description?.toLowerCase().includes(searchLower) ||
            product.purchasePrice.toString().includes(searchTerm) ||
            product.salePrice.toString().includes(searchTerm) ||
            product.quantity.toString().includes(searchTerm)
          );
      }
    });
  }, [products, searchTerm, filterField]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '400px' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!products.length) {
    return (
      <Paper elevation={2} sx={{ p: 3 }}>
        <Typography variant="h5" gutterBottom sx={{ mb: 3 }}>
          {t.products}
        </Typography>
        {error ? (
          <Alert severity="error">{error}</Alert>
        ) : (
          <Alert severity="info">Aucun produit trouvé</Alert>
        )}
      </Paper>
    );
  }

  return (
    <Paper elevation={2} sx={{ p: 3, overflow: 'hidden' }}>
      <Typography variant="h5" gutterBottom sx={{ mb: 3 }}>
        {t.products} ({products.length})
      </Typography>

      <Stack direction="row" spacing={2} sx={{ mb: 3 }}>
        <FormControl sx={{ minWidth: 120 }}>
          <InputLabel>Filtrer par</InputLabel>
          <Select
            value={filterField}
            label="Filtrer par"
            onChange={handleFilterChange}
            size="small"
          >
            <MenuItem value="all">Tout</MenuItem>
            <MenuItem value="code">Code</MenuItem>
            <MenuItem value="name">Nom</MenuItem>
            <MenuItem value="description">Description</MenuItem>
          </Select>
        </FormControl>

        <TextField
          fullWidth
          size="small"
          value={searchTerm}
          onChange={handleSearchChange}
          placeholder="Rechercher..."
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search size={20} />
              </InputAdornment>
            ),
            endAdornment: searchTerm && (
              <InputAdornment position="end">
                <IconButton onClick={clearSearch} size="small">
                  <X size={20} />
                </IconButton>
              </InputAdornment>
            ),
          }}
        />
      </Stack>

      <Box sx={{ 
        height: 600, 
        width: '100%',
        '& .MuiDataGrid-root': {
          border: 'none',
        },
        '& .MuiDataGrid-cell': {
          borderBottom: '1px solid #f0f0f0',
        },
        '& .MuiDataGrid-columnHeaders': {
          backgroundColor: '#f8f9fa',
          borderBottom: '2px solid #e9ecef',
        },
        '& .MuiDataGrid-virtualScroller': {
          overflow: 'auto',
        }
      }}>
        <DataGrid
          rows={filteredProducts}
          columns={columns}
          getRowId={(row) => row._id}
          disableRowSelectionOnClick
          pageSizeOptions={[5, 10, 25]}
          initialState={{
            pagination: {
              paginationModel: {
                pageSize: 10,
              },
            },
          }}
          sx={{
            boxShadow: 2,
            border: 2,
            borderColor: 'primary.light',
            '& .MuiDataGrid-cell:hover': {
              color: 'primary.main',
            },
          }}
        />
      </Box>

      {/* Edit Dialog */}
      <Dialog open={editDialogOpen} onClose={handleEditClose} maxWidth="sm" fullWidth>
        <DialogTitle>Modifier le produit</DialogTitle>
        <form onSubmit={handleEditSubmit}>
          <DialogContent>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <TextField
                name="code"
                label={t.productCode}
                value={editProduct?.code || ''}
                onChange={handleEditInputChange}
                fullWidth
                required
              />
              <TextField
                name="name"
                label={t.productName}
                value={editProduct?.name || ''}
                onChange={handleEditInputChange}
                fullWidth
                required
              />
              <TextField
                name="description"
                label={t.description}
                value={editProduct?.description || ''}
                onChange={handleEditInputChange}
                multiline
                rows={3}
                fullWidth
              />
              <TextField
                name="purchasePrice"
                label={t.purchasePrice}
                type="number"
                value={editProduct?.purchasePrice || ''}
                onChange={handleEditInputChange}
                fullWidth
                required
                inputProps={{ min: 0 }}
              />
              <TextField
                name="salePrice"
                label={t.salePrice}
                type="number"
                value={editProduct?.salePrice || ''}
                onChange={handleEditInputChange}
                fullWidth
                required
                inputProps={{ min: 0 }}
              />
              <TextField
                name="quantity"
                label={t.quantity}
                type="number"
                value={editProduct?.quantity || ''}
                onChange={handleEditInputChange}
                fullWidth
                required
                inputProps={{ min: 0 }}
              />
            </Box>
          </DialogContent>
          <DialogActions sx={{ p: 3, justifyContent: 'space-between' }}>
            <Button
              onClick={handleEditClose}
              variant="outlined"
              disabled={isUpdating}
              sx={{
                borderColor: '#E5E7EB',
                color: '#374151',
                '&:hover': {
                  borderColor: '#9CA3AF',
                  bgcolor: 'transparent'
                }
              }}
            >
              {t.cancel}
            </Button>
            <Button
              type="submit"
              variant="contained"
              disabled={isUpdating}
              sx={{
                bgcolor: '#1F2937',
                color: 'white',
                '&:hover': {
                  bgcolor: '#374151'
                }
              }}
            >
              {isUpdating ? (
                <CircularProgress size={24} color="inherit" />
              ) : (
                t.save
              )}
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={handleDeleteClose}>
        <DialogTitle>Confirmer la suppression</DialogTitle>
        <DialogContent>
          Êtes-vous sûr de vouloir supprimer le produit "{deleteProduct?.name}" ?
        </DialogContent>
        <DialogActions sx={{ p: 3, justifyContent: 'space-between' }}>
          <Button
            onClick={handleDeleteClose}
            variant="outlined"
            disabled={isDeleting}
            sx={{
              borderColor: '#E5E7EB',
              color: '#374151',
              '&:hover': {
                borderColor: '#9CA3AF',
                bgcolor: 'transparent'
              }
            }}
          >
            {t.cancel}
          </Button>
          <Button
            onClick={handleDeleteConfirm}
            variant="contained"
            disabled={isDeleting}
            sx={{
              bgcolor: '#DC2626',
              color: 'white',
              '&:hover': {
                bgcolor: '#B91C1C'
              }
            }}
          >
            {isDeleting ? (
              <CircularProgress size={24} color="inherit" />
            ) : (
              t.delete
            )}
          </Button>
        </DialogActions>
      </Dialog>

    </Paper>
  );
};
